import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios'
import { Message } from '@arco-design/web-vue'
import { useAuthStore } from '@/stores/auth'
import { isSessionInvalidError, isTokenInvalidError } from '@/utils/authError'

// 创建 axios 实例
// 在开发环境使用 Vite 代理，baseURL 为空字符串让请求发送到 Vite dev server
// 在生产环境使用环境变量配置的 API 地址
const useBff = (import.meta as any).env?.VITE_DEV_USE_BFF === 'true'
const isDev = (import.meta as any).env?.MODE === 'development'

const baseURL = (() => {
  if (useBff) {
    // 开发环境：使用相对路径，通过 Vite 代理转发
    // baseURL 设为 /bff，API 路径将拼接为 /bff/{api-path}
    if (isDev) {
      return '/bff'
    }
    // 生产环境：直接请求到 BFF 服务器
    const bffBase = (import.meta as any).env?.VITE_BFF_BASE || 'http://127.0.0.1:8080'
    return `${String(bffBase).replace(/\/$/, '')}/bff`
  }
  return (import.meta as any).env?.VITE_API_BASE_URL || ''
})()

const getBasePath = (url: string): string => {
  if (!url) {
    return ''
  }

  try {
    return new URL(url, 'http://localhost').pathname.replace(/\/+$/g, '')
  } catch (_error) {
    return String(url).replace(/\/+$/g, '')
  }
}

const normalizeApiPath = (url: string): string => {
  const normalizedAdminConfig = url.replace(
    /^(\/(api\/v1|v1)?\/admin\/config)\/config(?=\/|\?|$)/,
    '$1'
  )
  if (normalizedAdminConfig !== url) {
    return normalizedAdminConfig
  }

  if (!url.startsWith('/v1')) {
    return url
  }

  const basePath = getBasePath(baseURL)
  if (basePath.endsWith('/api/v1')) {
    return url.replace(/^\/v1(?=\/|$)/, '')
  }
  if (basePath === '/api') {
    return url
  }

  return url.replace(/^\/v1(?=\/|$)/, '/api/v1')
}

const service: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

const isAuthEndpoint = (url?: string) => {
  if (!url) {
    return false
  }
  const target = String(url)
  return (
    target.includes('/auth/login') ||
    target.includes('/auth/logout') ||
    target.includes('/auth/refresh') ||
    target.includes('/auth/me')
  )
}

const createClientTraceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const nextConfig = config as AxiosRequestConfig & {
      metadata?: { startTime: number; clientTraceId: string }
    }
    const clientTraceId = createClientTraceId()
    nextConfig.metadata = {
      startTime: Date.now(),
      clientTraceId,
    }

    if (!nextConfig.headers) {
      nextConfig.headers = {}
    }
    const requestHeaders = nextConfig.headers as Record<string, string>
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete requestHeaders['Content-Type']
      delete requestHeaders['content-type']
    }
    requestHeaders['X-Client-Trace-Id'] = clientTraceId

    if (typeof config.url === 'string') {
      config.url = normalizeApiPath(config.url)
    }

    // Debug: 在 BFF 模式下输出请求信息
    if (useBff && isDev) {
      console.log('[Request] BFF Mode Request:', {
        url: config.url,
        fullURL: `${baseURL}${config.url}`,
        withCredentials: config.withCredentials,
        hasCookie: document.cookie.includes('bff_sid'),
        cookiePreview: document.cookie.substring(0, 100)
      })
    }

    // 添加 token 到请求头
    // 在 BFF 模式下不从浏览器注入 Authorization（由 BFF 注入）
    if (!useBff && authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }

    // Add context headers (exclude auth endpoints and user profile endpoints)
    const excludedPaths = [
      '/auth/login',
      '/auth/refresh',
      '/auth/logout',
      '/auth/me',
      '/auth/check',
      '/auth/request-password-reset',
      '/auth/reset-password',
    ]
    const shouldExclude = excludedPaths.some(path => config.url?.includes(path))

    if (!shouldExclude) {
      // Add X-Current-Study header
      // 在 BFF 模式下不从浏览器发送 X-Current-*（由 BFF 注入）
    }

    return config
  },
  (error: AxiosError) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const requestConfig = response.config as AxiosRequestConfig & {
      metadata?: { startTime: number; clientTraceId: string }
    }
    const durationMs = requestConfig.metadata?.startTime
      ? Date.now() - requestConfig.metadata.startTime
      : null
    const serverTraceId = String(response.headers?.['x-trace-id'] || '')
    if ((import.meta as any).env?.DEV && durationMs !== null) {
      console.debug('[RequestMetrics]', {
        method: requestConfig.method,
        url: requestConfig.url,
        status: response.status,
        durationMs,
        clientTraceId: requestConfig.metadata?.clientTraceId,
        serverTraceId: serverTraceId || undefined,
      })
    }

    const res = response.data

    // 如果返回的状态码不是 2xx，显示错误
    if (response.status < 200 || response.status >= 300) {
      Message.error({
        content: res.message || 'Error',
        duration: 5000,
      })
      return Promise.reject(new Error(res.message || 'Error'))
    }

    return res
  },
  async (error: AxiosError) => {
    console.error('Response error:', error)
    const requestConfig = error.config as (AxiosRequestConfig & {
      metadata?: { startTime: number; clientTraceId: string }
    }) | undefined
    if ((import.meta as any).env?.DEV && requestConfig?.metadata?.startTime) {
      console.debug('[RequestMetrics:Error]', {
        method: requestConfig.method,
        url: requestConfig.url,
        durationMs: Date.now() - requestConfig.metadata.startTime,
        clientTraceId: requestConfig.metadata.clientTraceId,
      })
    }

    if (error.response) {
      const { status, data } = error.response as AxiosResponse<any>
      const requestConfig = error.config as AxiosRequestConfig & { _retry?: boolean }
      const requestUrl = requestConfig?.url || ''
      const isAuthError = status === 401 && !isAuthEndpoint(requestUrl)

      switch (status) {
        case 403: {
          const authStore = useAuthStore()
          const shouldRedirect = isSessionInvalidError(data, status, requestUrl)
          if (shouldRedirect) {
            Message.error(data?.message || '会话已失效，请重新登录')
            if (useBff) {
              await authStore.redirectToLogin('session_expired')
              break
            }

            Message.error('登录已过期，请重新登录')
            await authStore.redirectToLogin('token_refresh_failed')
            break
          }
          Message.error(data?.message || '拒绝访问')
          break
        }

        case 401: {
          const authStore = useAuthStore()
          if (!isAuthError) {
            break
          }

          // 在 BFF 模式下，401 由 BFF 层（代理 + 会话）处理，不在浏览器侧触发 refresh/logout
          if (useBff) {
            await authStore.redirectToLogin('session_expired')
            break
          }

          const shouldRefreshToken = isTokenInvalidError(data, requestConfig?.url)
          if (!shouldRefreshToken) {
            Message.error(data?.message || '请求未授权')
            await authStore.redirectToLogin('unauthorized')
            return Promise.reject(error)
          }

          // 避免重复刷新导致循环请求
          if (requestConfig?._retry) {
            Message.error('登录已过期，请重新登录')
            await authStore.redirectToLogin('token_refresh_failed')
            return Promise.reject(error)
          }

          // Mark this request as retried
          if (requestConfig) {
            requestConfig._retry = true
          }

          // Try to refresh token
          try {
            const refreshed = await authStore.refreshToken()
            if (!refreshed || !authStore.token) {
              throw new Error('Token refresh failed')
            }
            // Retry original request with new token
            if (requestConfig.headers) {
              requestConfig.headers.Authorization = `Bearer ${authStore.token}`
            }
            return service.request(requestConfig)
          } catch (refreshError) {
            Message.error('登录已过期，请重新登录')
            await authStore.redirectToLogin('token_refresh_failed')
            return Promise.reject(refreshError)
          }
        }

        case 404:
          Message.error('请求的资源不存在')
          break

        case 500:
          Message.error('服务器内部错误')
          break

        default:
          Message.error(data?.message || `请求失败 (${status})`)
      }
    } else if (error.request) {
      Message.error('网络错误，请检查您的网络连接')
    } else {
      Message.error('请求配置错误')
    }

    return Promise.reject(error)
  }
)

// 封装请求方法
const request = {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.get(url, config)
  },

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return service.post(url, data, config)
  },

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return service.put(url, data, config)
  },

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return service.patch(url, data, config)
  },

  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.delete(url, config)
  },
}

export default request
export { request }
